import React from "react";
import { Chip, Box } from "@mui/material";

interface ChipData {
  text?: string;
  leadingIconUrl?: string;
  [key: string]: any;
}

interface ChipListComponentProps {
  id?: string;
  cssClass?: string;
  enableDelete?: boolean;
  delete?: (args: { data: ChipData }) => void;
  chips?: ChipData[];
  children?: React.ReactNode;
}

// Helper components to maintain similar API structure
export const ChipsDirective: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const ChipDirective: React.FC<ChipData> = (props) => {
  return <span data-chip-props={JSON.stringify(props)} />;
};

function ChipListComponent({ id, enableDelete, delete: onDelete, chips, children }: ChipListComponentProps) {
  // Extract chip data from children (ChipsDirective > ChipDirective pattern)
  const extractChipsFromChildren = (children: React.ReactNode): ChipData[] => {
    const chipList: ChipData[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;

      const childProps = child.props as { children?: React.ReactNode } & ChipData;

      // If it's a ChipsDirective wrapper, process its children
      if (child.type === ChipsDirective || childProps.children) {
        const nestedChildren = childProps.children;
        if (Array.isArray(nestedChildren)) {
          nestedChildren.forEach((nestedChild: React.ReactNode) => {
            if (React.isValidElement(nestedChild)) {
              chipList.push(nestedChild.props as ChipData);
            }
          });
        } else if (React.isValidElement(nestedChildren)) {
          chipList.push(nestedChildren.props as ChipData);
        }
      } else {
        // Direct ChipDirective
        chipList.push(childProps as ChipData);
      }
    });

    return chipList;
  };

  const chipData = chips || extractChipsFromChildren(children);

  const handleDelete = (chip: ChipData) => {
    if (onDelete) {
      onDelete({ data: chip });
    }
  };

  return (
    <Box id={id} sx={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
      {chipData.map((chip, index) => (
        <Chip
          key={chip.key || index}
          label={chip.text}
          onDelete={enableDelete ? () => handleDelete(chip) : undefined}
          variant="outlined"
          size="small"
          avatar={
            chip.leadingIconUrl ? (
              <img
                src={chip.leadingIconUrl}
                alt=""
                style={{ width: "1.25rem", height: "1.25rem", borderRadius: "50%" }}
              />
            ) : undefined
          }
          sx={{
            "& .MuiChip-label": {
              paddingInlineStart: chip.leadingIconUrl ? 0 : undefined,
            },
          }}
        />
      ))}
    </Box>
  );
}

export default ChipListComponent;
